import { Link } from "react-router-dom";
import { Ban, CircleAlert, FileWarning, Globe2, MessageCircle, ShieldAlert, PackageX, Scale } from "lucide-react";
import MarketingLayout from "../components/marketing/MarketingLayout";
import { JsonLd, SITE_URL } from "../components/SEO";

const STRICTLY_PROHIBITED = [
  "Illegal goods, narcotics and psychotropic substances",
  "Explosives, firecrackers and fireworks of any kind",
  "Firearms, ammunition, weapon parts, knives and sharp weapons",
  "Flammable liquids — petrol, kerosene, diesel, paints, thinners, solvents, adhesives",
  "Flammable solids — matches, camphor, sulphur, magnesium",
  "Compressed gases, aerosols and gas cylinders (butane, LPG, oxygen, CO₂)",
  "Corrosive substances — acids, alkalis, wet-cell batteries",
  "Toxic, poisonous and infectious substances; medical or chemical waste",
  "Radioactive materials",
  "Live animals, birds, insects and reptiles",
  "Human or animal remains, organs and ashes",
  "Cash, currency notes, coins and bullion",
  "Gold, silver, platinum, precious and semi-precious stones",
  "High-value jewellery and watches",
  "Bearer bonds, negotiable instruments and lottery/gambling material",
  "Counterfeit, pirated or trademark-infringing goods",
  "Pornographic or obscene material",
  "Ivory and products from endangered wildlife (CITES-protected)",
  "Original passports, government IDs and other irreplaceable documents",
  "Alcohol, liquor and tobacco products (including gutkha/pan masala where banned)",
  "E-cigarettes, vapes and related refills (banned in India)",
  "Perishable food, fresh fruits/vegetables and items requiring cold-chain",
];

const INTL_PROHIBITED = [
  "Perfumes, attars, nail polish and other flammable liquids",
  "Aerosols and pressurised sprays (deodorants, sprays, sanitiser sprays)",
  "Standalone lithium batteries, power banks and loose cells",
  "Food items without documentation — perishables, dairy, meat, seeds, fresh produce",
  "Prescription medicines without valid documentation",
  "Plants, seeds, soil and agricultural products without certificates",
  "Poisons, laboratory chemicals and reagents",
  "Currency, cash equivalents and negotiable instruments",
  "Antiques, cultural artefacts and items of national heritage",
  "Hemp/CBD products and any item restricted by destination-country customs",
  "Any item prohibited by the destination country's import regulations",
];

const RESTRICTED = [
  ["Devices with installed lithium batteries", "accepted on most services — battery must be installed in the equipment, terminals protected"],
  ["Liquids, cosmetics and oils", "limited quantity, leak-proof double packing required"],
  ["Medicines and health supplements", "manufacturer-sealed with invoice; prescription drugs need supporting documents"],
  ["Packaged dry food", "non-perishable, manufacturer-packed with FSSAI/expiry label"],
  ["High-value electronics", "declared value and purchase invoice mandatory"],
  ["Plants and seeds (international)", "phytosanitary certificate required"],
  ["Religious items, idols and statues", "material restrictions apply (no ivory/sandalwood without permits)"],
  ["Magnetic materials and machinery parts", "may need air-carrier clearance"],
  ["Second-hand / used goods", "condition declaration required for customs"],
  ["Dry ice (for cold-chain)", "only with prior approval and proper marking"],
];

const Section = ({ icon: Icon, title, sub, children, accent, testId }) => (
  <section className="rounded-3xl border border-white/10 bg-shipany-panel p-6 sm:p-8" data-testid={testId}>
    <div className="flex items-start gap-3">
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${accent}`}><Icon className="h-5 w-5" /></span>
      <div>
        <h2 className="font-display text-lg font-bold text-white sm:text-xl">{title}</h2>
        <p className="mt-1 text-sm text-slate-400">{sub}</p>
      </div>
    </div>
    <div className="mt-5">{children}</div>
  </section>
);

export default function ProhibitedItems() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Prohibited & Restricted Items — ShipAny",
    url: `${SITE_URL}/prohibited-items`,
    description: "What you can and cannot send before booking your shipment — prohibited and restricted items for domestic and international courier shipments from India.",
    isPartOf: { "@id": `${SITE_URL}/#website` },
  };
  return (
    <MarketingLayout>
      <JsonLd data={jsonLd} />
      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-16" data-testid="prohibited-page">
        <div className="flex items-start gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-red-500/15 text-red-400"><PackageX className="h-6 w-6" /></span>
          <div>
            <h1 className="font-display text-4xl font-bold text-white sm:text-5xl" data-testid="prohibited-title">Prohibited & Restricted Items</h1>
            <p className="mt-3 max-w-2xl text-base text-slate-300 sm:text-lg">Know what you can and cannot send before booking your shipment.</p>
          </div>
        </div>

        <div className="mt-8 flex items-start gap-3 rounded-2xl border border-amber-400/30 bg-amber-400/10 p-5" data-testid="prohibited-notice">
          <CircleAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />
          <p className="text-sm leading-relaxed text-amber-100">
            <span className="font-bold">Important:</span> Shipment acceptance is subject to applicable law, customs regulations, courier-partner rules and destination-country requirements. An item accepted in one country or by one courier partner may be restricted elsewhere.
          </p>
        </div>

        <div className="mt-8 space-y-6">
          <Section icon={Ban} title="Strictly Prohibited — every shipment" sub="These items cannot be shipped on any domestic or international service, under any circumstances." accent="bg-red-500/15 text-red-400" testId="prohibited-all">
            <ul className="grid gap-2 sm:grid-cols-2">
              {STRICTLY_PROHIBITED.map((item) => (
                <li key={item} className="flex items-start gap-2 rounded-xl bg-white/5 px-3 py-2.5 text-sm text-slate-300">
                  <Ban className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-400" />{item}
                </li>
              ))}
            </ul>
          </Section>

          <Section icon={Globe2} title="Additionally Prohibited — international shipments" sub="On top of the list above, these cannot be sent on international services." accent="bg-orange-500/15 text-orange-400" testId="prohibited-intl">
            <ul className="grid gap-2 sm:grid-cols-2">
              {INTL_PROHIBITED.map((item) => (
                <li key={item} className="flex items-start gap-2 rounded-xl bg-white/5 px-3 py-2.5 text-sm text-slate-300">
                  <Ban className="mt-0.5 h-3.5 w-3.5 shrink-0 text-orange-400" />{item}
                </li>
              ))}
            </ul>
          </Section>

          <Section icon={FileWarning} title="Restricted — case-by-case acceptance" sub="Shippable only when the stated conditions are met. Approval or documents may be required before pickup." accent="bg-shipany-cyan/15 text-shipany-cyan" testId="restricted-list">
            <ul className="space-y-2">
              {RESTRICTED.map(([item, cond]) => (
                <li key={item} className="flex flex-col gap-1 rounded-xl bg-white/5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <span className="flex items-start gap-2 text-sm font-semibold text-white"><ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-shipany-cyan" />{item}</span>
                  <span className="text-xs text-slate-400 sm:max-w-[45%] sm:text-right">{cond}</span>
                </li>
              ))}
            </ul>
          </Section>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <div className="flex items-start gap-3 rounded-2xl border border-white/10 bg-shipany-panel p-5" data-testid="prohibited-legal">
            <Scale className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
            <p className="text-xs leading-relaxed text-slate-400">
              Booking a prohibited item may lead to shipment seizure, return at the sender's cost, penalties and legal action. Declaring contents falsely to bypass these rules voids insurance and all carrier claims. Refer to our <Link to="/terms" className="text-shipany-cyan hover:underline">Terms & Conditions</Link> and <Link to="/shipping-policy" className="text-shipany-cyan hover:underline">Shipping Policy</Link>.
            </p>
          </div>
          <div className="flex items-start gap-3 rounded-2xl border border-shipany-cyan/25 bg-shipany-cyan/10 p-5" data-testid="prohibited-help">
            <MessageCircle className="mt-0.5 h-5 w-5 shrink-0 text-shipany-cyan" />
            <div>
              <p className="text-sm font-bold text-white">Not sure about your item?</p>
              <p className="mt-1 text-xs leading-relaxed text-slate-300">Share the item description with our team before booking — we will confirm acceptance and any documents needed.</p>
              <Link to="/contact" data-testid="prohibited-contact-link" className="mt-2 inline-block text-xs font-bold text-shipany-cyan hover:underline">Contact support →</Link>
            </div>
          </div>
        </div>
      </div>
    </MarketingLayout>
  );
}
